import { useCallback, useState } from 'react';
import { toast } from 'sonner';

import { X } from 'lucide-react';

import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select';
import { Textarea } from '@/components/ui/textarea';
import { useAuth } from '@/contexts/AuthContext';
import { submitContactSupport } from '@/lib/apiClient';

const CATEGORIES = [
  'Bug Report',
  'Feature Request',
  'Account Issue',
  'General Question',
] as const;

const MIN_MESSAGE_LENGTH = 10;

interface ContactSupportModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function ContactSupportModal({
  isOpen,
  onClose,
}: ContactSupportModalProps) {
  const { user, activeClinicName } = useAuth();
  const [message, setMessage] = useState('');
  const [category, setCategory] = useState<string | undefined>(undefined);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const resetForm = useCallback(() => {
    setMessage('');
    setCategory(undefined);
  }, []);

  const handleClose = useCallback(() => {
    resetForm();
    onClose();
  }, [onClose, resetForm]);

  const handleSubmit = useCallback(async () => {
    if (message.trim().length < MIN_MESSAGE_LENGTH) return;

    setIsSubmitting(true);
    try {
      await submitContactSupport({
        message: message.trim(),
        category,
      });
      toast.success("Your message has been sent. We'll get back to you soon.");
      handleClose();
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : 'Failed to send message. Please try again.'
      );
    } finally {
      setIsSubmitting(false);
    }
  }, [message, category, handleClose]);

  const isValid = message.trim().length >= MIN_MESSAGE_LENGTH;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Contact Support"
      actionLabel="Send Message"
      actionLoadingLabel="Sending..."
      onAction={handleSubmit}
      isActionLoading={isSubmitting}
      isActionDisabled={!isValid}
      cancelLabel="Cancel"
    >
      <div className="space-y-5">
        {/* Pre-filled user info */}
        <div className="rounded-lg bg-background-strong p-4 space-y-2">
          <div className="flex gap-2 text-sm">
            <span className="text-body-base font-medium">Name:</span>
            <span className="text-default">
              {user?.firstName} {user?.lastName}
            </span>
          </div>
          <div className="flex gap-2 text-sm">
            <span className="text-body-base font-medium">Email:</span>
            <span className="text-default">{user?.email}</span>
          </div>
          {activeClinicName && (
            <div className="flex gap-2 text-sm">
              <span className="text-body-base font-medium">Clinic:</span>
              <span className="text-default">{activeClinicName}</span>
            </div>
          )}
        </div>

        {/* Category selector */}
        <div className="space-y-1.5">
          <label
            className="text-caption-label font-medium"
            htmlFor="support-category"
          >
            Category (optional)
          </label>
          <div className="flex items-center gap-2">
            <Select value={category ?? ''} onValueChange={setCategory}>
              <SelectTrigger id="support-category" className="flex-1">
                <SelectValue placeholder="Select a category" />
              </SelectTrigger>
              <SelectContent>
                {CATEGORIES.map(cat => (
                  <SelectItem key={cat} value={cat}>
                    {cat}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {category && (
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-9 w-9 shrink-0"
                onClick={() => setCategory(undefined)}
                aria-label="Clear category"
              >
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>

        {/* Message textarea */}
        <div className="space-y-1.5">
          <label
            className="text-caption-label font-medium"
            htmlFor="support-message"
          >
            Message
          </label>
          <Textarea
            id="support-message"
            placeholder="Describe what you need help with..."
            value={message}
            onChange={e => setMessage(e.target.value)}
            rows={5}
            maxLength={2000}
            disabled={isSubmitting}
          />
          <div className="flex justify-between text-xs text-body-base">
            <span>
              {message.trim().length < MIN_MESSAGE_LENGTH &&
              message.trim().length > 0
                ? `At least ${MIN_MESSAGE_LENGTH} characters required`
                : ' '}
            </span>
            <span>{message.trim().length}/2000</span>
          </div>
        </div>
      </div>
    </Modal>
  );
}
